import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QrCode, AlertTriangle, ArrowRight, FlaskConical } from 'lucide-react';

// =====================================================================
// A PORTA DO QR CODE — rota `/q/:codigo`
//
// É aqui que chega quem apontou a câmera para o adesivo colado no equipamento.
// Esta tela não desenha nada de permanente: ela resolve o código e ENCAMINHA
// para a abertura de chamado com o equipamento já escolhido.
//
// ⚠️⚠️ POR QUE ELA MORA FORA DO `/chamados`. O adesivo é imutável por anos; a
//    rota de um módulo é a coisa que mais muda (o hub do Chamados foi
//    reorganizado três vezes em agosto/2026). O `/q` é um ponto de entrada
//    perene: ele RESOLVE para a tela de hoje e pode resolver para outra amanhã,
//    sem reimprimir 1.000 adesivos. Mudar o destino é mudar UMA linha aqui.
//
// ⚠️ QUEM ESCANEIA COSTUMA ESTAR DESLOGADO — a sessão do portal dura 8h e isto
//    roda no celular do gerente. Quem resolve isso NÃO é este arquivo: é o
//    `PrivateRoute` do App.js, que guarda o destino em `state.from` e o `Login`,
//    que volta para cá depois de entrar. Por isso esta rota precisa estar DENTRO
//    do PrivateRoute — fora dele, a pessoa cairia num login sem contexto e
//    recomeçaria do zero, que é o jeito mais rápido de matar o projeto no uso.
//
// ⚠️ E NENHUM CAMINHO AQUI TERMINA EM BECO. Do outro lado está o gerente de pé
//    na loja com o aparelho quebrado na mão: "etiqueta não encontrada" e ponto
//    final o manda de volta ao WhatsApp, que é o que o módulo existe para matar.
//    Todo diagnóstico termina num botão que leva a algum lugar útil.
// =====================================================================

const COR = 'var(--ch-cor)';
const COR_ESCURA = 'var(--ch-cor-escura)';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const ChamadosEtiquetaEntrada = () => {
    const { codigo } = useParams();
    const navigate = useNavigate();
    const [estado, setEstado] = useState({ carregando: true });

    useEffect(() => {
        let vivo = true;
        fetch(`/api/chamados/etiquetas/resolver/${encodeURIComponent(codigo)}`, { headers: auth() })
            .then(async (r) => ({ ok: r.ok, dados: await r.json().catch(() => ({})) }))
            .then(({ ok, dados }) => {
                if (!vivo) return;
                // ⚠⚠ A TRAVA DO ADESIVO `MAN` SAIU EM 17/09/2026, junto com a
                //    abertura da fila de Manutenção — e as duas coisas têm de
                //    andar no MESMO deploy. Enquanto a abertura gravava
                //    `EQUIPE = 'TI'` fixo, um adesivo `MAN` encaminhado para cá
                //    faria nascer, em silêncio, um chamado na fila errada com o
                //    equipamento certo. A trava era a proteção, não um bug.
                //    ⚠️ O que substitui a proteção agora é a EQUIPE viajar junto:
                //    o `departamento` do adesivo vai no `state` abaixo e o hub o
                //    traduz para a fila ('MAN' → 'Manutencao').
                if (dados.situacao === 'ok') {
                    // ⚠️ `replace: true` — a porta do QR não fica no histórico. Sem
                    //    isso, o "voltar" do celular traria a pessoa para cá e ela
                    //    seria reencaminhada, num laço que parece travamento.
                    navigate('/chamados', {
                        replace: true,
                        // ⚠️ `equipe` VEM DO SERVIDOR pronta ('TI' | 'Manutencao'),
                        //    nunca derivada aqui: a tradução do departamento do
                        //    adesivo para a fila do chamado mora num lugar só
                        //    (`equipeDoDepartamento`, no módulo puro da etiqueta).
                        state: {
                            etiqueta: {
                                codigo: dados.codigo,
                                departamento: dados.departamento,
                                equipe: dados.equipe,
                                teste: dados.teste,
                                ativo: dados.ativo,
                            },
                        },
                    });
                    return;
                }
                setEstado({
                    carregando: false,
                    situacao: dados.situacao || 'erro',
                    mensagem: dados.message || (ok ? 'Não foi possível ler a etiqueta.' : 'Erro ao ler a etiqueta.'),
                    dados,
                });
            })
            .catch(() => {
                if (vivo) {
                    setEstado({
                        carregando: false,
                        situacao: 'erro',
                        mensagem: 'Não foi possível falar com o portal. Verifique a conexão e tente de novo.',
                    });
                }
            });
        return () => { vivo = false; };
    }, [codigo, navigate]);

    const caixa = {
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', background: '#f8fafc', boxSizing: 'border-box',
    };
    const cartao = {
        background: '#fff', borderRadius: '16px', padding: '28px 24px',
        width: '100%', maxWidth: '420px', textAlign: 'center',
        boxShadow: '0 10px 30px rgba(15,23,42,.08)',
    };
    const botao = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        width: '100%', padding: '13px 16px', marginTop: '18px', borderRadius: '10px',
        border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: 600,
        background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`, color: '#fff',
    };
    const codigoCss = {
        fontFamily: 'ui-monospace, Consolas, monospace', fontVariantNumeric: 'tabular-nums',
        fontWeight: 700, fontSize: '17px', letterSpacing: '.04em', color: '#1e293b',
    };

    if (estado.carregando) {
        return (
            <div style={caixa}>
                <div style={cartao}>
                    <QrCode size={40} color={COR} />
                    <p style={{ margin: '14px 0 0', fontSize: '15px', color: '#475569' }}>
                        Identificando o equipamento…
                    </p>
                    <p style={{ margin: '6px 0 0', ...codigoCss }}>{String(codigo || '').toUpperCase()}</p>
                </div>
            </div>
        );
    }

    // O código como está IMPRESSO no adesivo (`M-0001`), com o que veio na URL
    // como último recurso.
    //
    // ⚠️ A pessoa chegou aqui ESCANEANDO o adesivo e está com ele na mão: mostrar
    //    `MAN-00001` (a forma canônica, que é a que vai dentro do QR) a faria
    //    achar que leu a etiqueta errada. Enquanto o servidor não responde, o
    //    fallback é o que está na URL — não há o que derivar antes disso.
    const codigoNaTela = estado.dados?.codigo_impresso || String(codigo || '').toUpperCase();

    // ⚠️ Cada situação tem o SEU próximo passo, e é por isso que elas não se
    //    fundem num "erro" só: o que a pessoa faz em cada caso é diferente.
    const ehDemonstracao = estado.dados?.teste;

    // O que vai para o hub quando a etiqueta não resolveu o equipamento.
    //
    // ⚠️ Leva o DEPARTAMENTO mesmo sem ativo — é o que faz a portaria ("abrir
    //    para qual área?") não perguntar o que o próprio adesivo já respondeu.
    // ⚠️ `null` quando o código nem é nosso (`invalida`): aí o departamento é
    //    genuinamente desconhecido, e a portaria volta a ser a pergunta certa.
    //    Chutar "TI" aqui abriria chamado na fila errada em silêncio.
    const destinoSemAtivo = estado.dados?.departamento
        ? {
            etiqueta: {
                codigo: estado.dados.codigo,
                departamento: estado.dados.departamento,
                equipe: estado.dados.equipe,
                ativo: null,
            },
        }
        : undefined;

    return (
        <div style={caixa}>
            <div style={cartao}>
                <AlertTriangle size={38} color="#d97706" />
                <h1 style={{ margin: '14px 0 6px', fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                    {estado.situacao === 'manutencao_em_implantacao' ? 'Fila de Manutenção em implantação'
                        : estado.situacao === 'sem_vinculo' ? 'Etiqueta ainda sem equipamento'
                        : estado.situacao === 'desconhecida' ? 'Etiqueta não cadastrada'
                            : estado.situacao === 'baixada' ? 'Etiqueta fora de uso'
                                : estado.situacao === 'ativo_ausente' ? 'Equipamento não encontrado'
                                    : estado.situacao === 'invalida' ? 'Código não reconhecido'
                                        : 'Não foi possível ler a etiqueta'}
                </h1>
                <p style={{ margin: '0 0 4px', ...codigoCss }}>{codigoNaTela}</p>
                {ehDemonstracao && (
                    <p style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px', margin: '8px 0 0',
                        fontSize: '11.5px', fontWeight: 700, color: '#92400e',
                        background: '#fef3c7', padding: '3px 9px', borderRadius: '20px',
                    }}>
                        <FlaskConical size={12} /> ETIQUETA DE DEMONSTRAÇÃO
                    </p>
                )}
                <p style={{ margin: '12px 0 0', fontSize: '14.5px', color: '#475569', lineHeight: 1.55 }}>
                    {estado.mensagem}
                </p>

                {/* ⚠️ A SAÍDA É SEMPRE ABRIR O CHAMADO. O equipamento é campo
                    OPCIONAL na abertura, de propósito — então nada disso impede a
                    loja de registrar o problema. Etiqueta com defeito não pode
                    virar chamado que não existe.
                    ⚠️⚠️ E LEVA O DEPARTAMENTO JUNTO, mesmo sem equipamento: o
                    código do adesivo já diz se é TI ou Manutenção, então
                    perguntar de novo na portaria é pedir à pessoa uma informação
                    que ela acabou de nos dar. Sem este `state`, a portaria
                    voltava a aparecer justamente no caminho de exceção — que é
                    onde a pessoa já está contrariada. */}
                {/* ⚠⚠ NA FILA EM IMPLANTAÇÃO NÃO HÁ ESTE BOTÃO, e é o mesmo motivo
                    da trava antiga: ele leva à abertura, e a abertura na fila de
                    Manutenção devolve 403 para quem não toca a implantação.
                    Oferecê-lo seria convidar a pessoa a um caminho que morre —
                    depois de ela ter escaneado o adesivo certo.
                    ⚠️ Nas outras situações ele vale para os DOIS departamentos: o
                    `state` carrega a fila e a abertura nasce na certa. */}
                {estado.situacao !== 'manutencao_em_implantacao' && (
                    <>
                        <button type="button" style={botao}
                            onClick={() => navigate('/chamados', { replace: true, state: destinoSemAtivo })}>
                            Abrir chamado mesmo assim <ArrowRight size={17} />
                        </button>
                        <p style={{ margin: '12px 0 0', fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.5 }}>
                            Você escolhe o equipamento na lista. Avise o TI que esta etiqueta precisa
                            de conferência.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChamadosEtiquetaEntrada;
